import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, Ban, Printer } from 'lucide-react';
import '../styles/VoucherForms.css';
import apiClient from '../services/api';

const CashVoucher = () => {
  // Session management
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // State management
  const [vouchers, setVouchers] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [otherAccounts, setOtherAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [voucherMode, setVoucherMode] = useState('payment');
  
  // Form data
  const [formData, setFormData] = useState({
    acjournalid: null,
    companyid: companyId || null,
    fyearid: yearId || null,
    voucherno: '',
    transdate: new Date().toISOString().split('T')[0],
    vouchertype: 'CV',
    remarks: '',
    cashAccountId: '',
    details: []
  });

  // Detail line being edited
  const [currentDetail, setCurrentDetail] = useState({
    acheadid: '',
    amount: '',
    remarks: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCashAccounts(),
        fetchOtherAccounts(),
        fetchVouchers()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCashAccounts = async () => {
    try {
      const response = await apiClient.get('/Acheads.php', {
        params: {
          companyid: companyId,
          accounttype: 'Cash'
        }
      });
      
      if (response.data.success) {
        setCashAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching cash accounts:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const fetchOtherAccounts = async () => {
    try {
      const response = await apiClient.get('/Acheads.php', {
        params: {
          companyid: companyId,
          excludetype: 'Cash'
        }
      });
      
      if (response.data.success) {
        setOtherAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching other accounts:', error);
    }
  };

  const fetchVouchers = async () => {
    try {
      const response = await apiClient.get('/Vouchers.php', {
        params: {
          companyid: companyId,
          fyearid: yearId,
          vouchertype: voucherMode === 'payment' ? 'CV' : 'CR'
        }
      });
      
      if (response.data.success) {
        setVouchers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const getNextVoucherNumber = async () => {
    try {
      const voucherType = voucherMode === 'payment' ? 'CV' : 'CR';
      const response = await apiClient.get('/Vouchers.php', {
        params: {
          nextvoucher: true,
          companyid: companyId,
          fyearid: yearId,
          vouchertype: voucherType
        }
      });
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          voucherno: response.data.data.voucherno,
          vouchertype: voucherType
        }));
      }
    } catch (error) {
      console.error('Error getting next voucher number:', error);
    }
  };

  const handleVoucherModeChange = (mode) => {
    setVoucherMode(mode);
    setFormData(prev => ({
      ...prev,
      vouchertype: mode === 'payment' ? 'CV' : 'CR'
    }));
  };

  const handleNew = async () => {
    resetForm();
    await getNextVoucherNumber();
    setEditMode(false);
    setShowModal(true);
  };

  const handleEdit = async (voucher) => {
    try {
      const response = await apiClient.get(`/Vouchers.php`, {
        params: { id: voucher.acjournalid }
      });
      
      if (response.data.success) {
        const { master, details } = response.data.data;
        
        setFormData({
          acjournalid: master.acjournalid,
          companyid: master.companyid,
          fyearid: master.fyearid,
          voucherno: master.voucherno,
          transdate: master.transdate,
          vouchertype: master.vouchertype,
          remarks: master.remarks || '',
          cashAccountId: details.length > 0 ? details[0].acheadid : '',
          details: details.slice(1)
        });
        
        setVoucherMode(master.vouchertype === 'CR' ? 'receipt' : 'payment');
        setEditMode(true);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error loading voucher:', error);
      alert('Error loading voucher details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this voucher?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/Vouchers.php', {
        data: { acjournalid: id }
      });
      
      if (response.data.success) {
        alert('Voucher deleted successfully!');
        fetchVouchers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting voucher:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting voucher. Please try again.');
      }
    }
  };

  const handlePrint = () => {
    if (formData.details.length === 0) {
      alert('Please add transaction details before printing');
      return;
    }

    if (!formData.cashAccountId) {
      alert('Please select a cash account before printing');
      return;
    }

    const cashAccount = cashAccounts.find(a => a.acheadid === parseInt(formData.cashAccountId));
    const voucherTypeText = voucherMode === 'payment' ? 'Cash Payment' : 'Cash Receipt';

    const printWindow = window.open('', '', 'height=600,width=800');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cash Voucher - ${formData.voucherno}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            padding: 20mm;
            background: white;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #000;
            padding-bottom: 20px;
          }
          
          .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .voucher-title {
            font-size: 18px;
            font-weight: bold;
            margin-top: 10px;
            color: ${voucherMode === 'payment' ? '#991b1b' : '#065f46'};
          }
          
          .voucher-info {
            display: flex;
            justify-content: space-between;
            margin: 20px 0;
            padding: 15px;
            background: #f5f5f5;
            border: 1px solid #ddd;
          }
          
          .info-item {
            display: flex;
            flex-direction: column;
          }
          
          .info-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 4px;
          }
          
          .info-value {
            font-size: 14px;
            font-weight: bold;
            font-family: 'Courier New', monospace;
          }
          
          .cash-account-box {
            background: ${voucherMode === 'payment' ? '#fee2e2' : '#d1fae5'};
            border-left: 4px solid ${voucherMode === 'payment' ? '#dc2626' : '#059669'};
            padding: 15px;
            margin: 20px 0;
          }
          
          .cash-account-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 8px;
            font-weight: bold;
          }
          
          .cash-account-name {
            font-size: 16px;
            font-weight: bold;
            color: #1f2937;
          }
          
          .details-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          
          .details-table th {
            background: #000;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-size: 12px;
            text-transform: uppercase;
            font-weight: bold;
          }
          
          .details-table td {
            padding: 10px 8px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
          }
          
          .details-table tbody tr:hover {
            background: #f9f9f9;
          }
          
          .details-table .serial {
            text-align: center;
            width: 40px;
          }
          
          .details-table .account-name {
            font-weight: 600;
          }
          
          .details-table .amount {
            text-align: right;
            font-family: 'Courier New', monospace;
            font-weight: bold;
            color: ${voucherMode === 'payment' ? '#dc2626' : '#059669'};
          }
          
          .details-table .remarks {
            font-style: italic;
            color: #666;
          }
          
          .totals-section {
            margin: 20px 0;
            border-top: 3px double #000;
            padding-top: 15px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 15px;
            font-size: 18px;
            font-weight: bold;
            background: ${voucherMode === 'payment' ? '#fee2e2' : '#d1fae5'};
            border: 2px solid ${voucherMode === 'payment' ? '#dc2626' : '#059669'};
            border-radius: 8px;
          }
          
          .total-label {
            font-weight: bold;
          }
          
          .total-value {
            font-family: 'Courier New', monospace;
            font-weight: bold;
            color: ${voucherMode === 'payment' ? '#991b1b' : '#065f46'};
          }
          
          .remarks-section {
            margin: 20px 0;
            padding: 15px;
            background: #f9f9f9;
            border-left: 4px solid #667eea;
          }
          
          .remarks-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 8px;
            font-weight: bold;
          }
          
          .remarks-text {
            font-size: 13px;
            line-height: 1.6;
          }
          
          .signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 60px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
          }
          
          .signature-box {
            text-align: center;
            width: 200px;
          }
          
          .signature-line {
            border-top: 2px solid #000;
            margin-top: 60px;
            margin-bottom: 8px;
          }
          
          .signature-label {
            font-size: 12px;
            color: #666;
          }
          
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 11px;
            color: #999;
            border-top: 1px solid #ddd;
            padding-top: 15px;
          }
          
          .payment-badge {
            display: inline-block;
            background: #991b1b;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }
          
          .receipt-badge {
            display: inline-block;
            background: #065f46;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }
          
          @media print {
            body {
              padding: 0;
            }
            
            .print-header {
              page-break-after: avoid;
            }
            
            .details-table {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="company-name">${session?.companyname || 'Company Name'}</div>
          <div class="voucher-title">${voucherTypeText.toUpperCase()} VOUCHER</div>
        </div>
        
        <div class="voucher-info">
          <div class="info-item">
            <span class="info-label">Voucher No</span>
            <span class="info-value">${formData.voucherno}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Date</span>
            <span class="info-value">${new Date(formData.transdate).toLocaleDateString('en-GB')}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Type</span>
            <span class="info-value">${voucherMode === 'payment' ? 'CV' : 'CR'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Status</span>
            <span class="${voucherMode === 'payment' ? 'payment-badge' : 'receipt-badge'}">
              ${voucherTypeText.toUpperCase()}
            </span>
          </div>
        </div>
        
        <div class="cash-account-box">
          <div class="cash-account-label">${voucherMode === 'payment' ? 'Cash Paid From' : 'Cash Received To'}</div>
          <div class="cash-account-name">${cashAccount?.acheadname || 'Cash Account'}</div>
        </div>
        
        <table class="details-table">
          <thead>
            <tr>
              <th class="serial">#</th>
              <th>Account Name</th>
              <th style="width: 150px; text-align: right;">Amount</th>
              <th style="width: 250px;">Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${formData.details.map((detail, index) => `
              <tr>
                <td class="serial">${index + 1}</td>
                <td class="account-name">${detail.acheadname}</td>
                <td class="amount">₹ ${parseFloat(detail.amount).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                <td class="remarks">${detail.remarks || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals-section">
          <div class="total-row">
            <span class="total-label">Total Amount:</span>
            <span class="total-value">₹ ${calculateTotal().toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
          </div>
        </div>
        
        ${formData.remarks ? `
          <div class="remarks-section">
            <div class="remarks-label">Remarks</div>
            <div class="remarks-text">${formData.remarks}</div>
          </div>
        ` : ''}
        
        <div class="signatures">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">Prepared By</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">Verified By</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">Approved By</div>
          </div>
        </div>
        
        <div class="footer">
          <p>This is a computer-generated document. No signature is required.</p>
          <p>Printed on: ${new Date().toLocaleString('en-GB')}</p>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const addDetailLine = () => {
    if (!currentDetail.acheadid || !currentDetail.amount) {
      alert('Please select an account and enter an amount');
      return;
    }
    
    const account = otherAccounts.find(a => a.acheadid === parseInt(currentDetail.acheadid));
    if (!account) {
      alert('Invalid account selected');
      return;
    }
    
    const newDetail = {
      acheadid: parseInt(currentDetail.acheadid),
      acheadname: account.acheadname,
      amount: parseFloat(currentDetail.amount),
      remarks: currentDetail.remarks
    };
    
    setFormData(prev => ({
      ...prev,
      details: [...prev.details, newDetail]
    }));
    
    setCurrentDetail({
      acheadid: '',
      amount: '',
      remarks: ''
    });
  };

  const removeDetailLine = (index) => {
    setFormData(prev => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return formData.details.reduce((sum, detail) => sum + parseFloat(detail.amount || 0), 0);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.voucherno.trim()) {
      newErrors.voucherno = 'Voucher number is required';
    }
    
    if (!formData.transdate) {
      newErrors.transdate = 'Transaction date is required';
    }
    
    if (!formData.cashAccountId) {
      newErrors.cashAccountId = 'Cash account is required';
    }
    
    if (formData.details.length === 0) {
      newErrors.details = 'At least one detail line is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const cashAccountAmount = voucherMode === 'receipt' ? calculateTotal() : -calculateTotal();
      
      const voucherDetails = [
        {
          acheadid: parseInt(formData.cashAccountId),
          amount: cashAccountAmount,
          remarks: 'Cash Account'
        },
        ...formData.details.map(d => ({
          acheadid: d.acheadid,
          amount: voucherMode === 'receipt' ? -d.amount : d.amount,
          remarks: d.remarks || ''
        }))
      ];
      
      const payload = {
        companyid: companyId,
        fyearid: yearId,
        voucherno: formData.voucherno,
        transdate: formData.transdate,
        vouchertype: formData.vouchertype,
        remarks: formData.remarks,
        details: voucherDetails
      };
      
      if (editMode) {
        payload.acjournalid = formData.acjournalid;
      }
      
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Vouchers.php', payload);
      
      if (response.data.success) {
        alert(editMode ? 'Voucher updated successfully!' : 'Voucher created successfully!');
        setShowModal(false);
        resetForm();
        fetchVouchers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving voucher:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving voucher. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      acjournalid: null,
      companyid: companyId || null,
      fyearid: yearId || null,
      voucherno: '',
      transdate: new Date().toISOString().split('T')[0],
      vouchertype: voucherMode === 'payment' ? 'CV' : 'CR',
      remarks: '',
      cashAccountId: '',
      details: []
    });
    setCurrentDetail({
      acheadid: '',
      amount: '',
      remarks: ''
    });
    setErrors({});
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (!companyId || !yearId) {
    return (
      <div className="voucher-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredVouchers = vouchers.filter(v =>
    v.voucherno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.remarks?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="voucher-container">
      <div className="voucher-header">
        <div className="header-left">
          <h2>Cash Voucher</h2>
          <div className="voucher-mode-toggle">
            <button
              className={`mode-btn ${voucherMode === 'payment' ? 'active' : ''}`}
              onClick={() => handleVoucherModeChange('payment')}
            >
              Payment
            </button>
            <button
              className={`mode-btn ${voucherMode === 'receipt' ? 'active' : ''}`}
              onClick={() => handleVoucherModeChange('receipt')}
            >
              Receipt
            </button>
          </div>
        </div>
        <button className="btn-primary" onClick={handleNew}>
          <Plus size={20} />
          New Voucher
        </button>
      </div>

      <div className="voucher-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search vouchers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredVouchers.length} vouchers
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading vouchers...</div>
      ) : filteredVouchers.length === 0 ? (
        <div className="empty-state">
          <p>No vouchers found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Voucher No</th>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Remarks</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.acjournalid}>
                  <td className="font-mono">{voucher.voucherno}</td>
                  <td>{new Date(voucher.transdate).toLocaleDateString()}</td>
                  <td>
                    <span className={`type-badge ${voucher.vouchertype === 'CR' ? 'receipt' : 'payment'}`}>
                      {voucher.voucher_description}
                    </span>
                  </td>
                  <td className="amount-cell">{parseFloat(voucher.total_amount || 0).toFixed(2)}</td>
                  <td>{voucher.remarks || '-'}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(voucher)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(voucher.acjournalid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large voucher-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editMode ? 'Edit' : 'New'} {voucherMode === 'payment' ? 'Cash Payment' : 'Cash Receipt'}
              </h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="voucher-form">
              <div className="form-section">
                <h4>Voucher Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="voucherno">
                      Voucher No <span className="required">*</span>
                      <span className="auto-badge">Auto</span>
                    </label>
                    <div className="voucher-no-wrapper">
                      <input
                        type="text"
                        id="voucherno"
                        value={formData.voucherno}
                        readOnly
                        className="voucher-no-locked"
                        placeholder="Auto-generating..."
                      />
                      <span className="lock-icon" title="Auto-generated">🔒</span>
                    </div>
                    {errors.voucherno && <span className="error-message">{errors.voucherno}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="transdate">
                      Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      id="transdate"
                      value={formData.transdate}
                      onChange={(e) => setFormData({ ...formData, transdate: e.target.value })}
                      className={errors.transdate ? 'error' : ''}
                    />
                    {errors.transdate && <span className="error-message">{errors.transdate}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="cashAccountId">
                      Cash Account <span className="required">*</span>
                    </label>
                    <select
                      id="cashAccountId"
                      value={formData.cashAccountId}
                      onChange={(e) => setFormData({ ...formData, cashAccountId: e.target.value })}
                      className={errors.cashAccountId ? 'error' : ''}
                    >
                      <option value="">-- Select Cash Account --</option>
                      {cashAccounts.map((account) => (
                        <option key={account.acheadid} value={account.acheadid}>
                          {account.acheadname} ({account.acheadcode})
                        </option>
                      ))}
                    </select>
                    {errors.cashAccountId && <span className="error-message">{errors.cashAccountId}</span>}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Details <span className="required">*</span></h4>
                
                <div className="detail-input-row">
                  <select
                    value={currentDetail.acheadid}
                    onChange={(e) => setCurrentDetail({ ...currentDetail, acheadid: e.target.value })}
                    style={{ flex: 2 }}
                  >
                    <option value="">-- Select Account --</option>
                    {otherAccounts.map((account) => (
                      <option key={account.acheadid} value={account.acheadid}>
                        {account.acheadname}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    value={currentDetail.amount}
                    onChange={(e) => setCurrentDetail({ ...currentDetail, amount: e.target.value })}
                    placeholder="Amount"
                    step="0.01"
                    style={{ flex: 1 }}
                  />

                  <input
                    type="text"
                    value={currentDetail.remarks}
                    onChange={(e) => setCurrentDetail({ ...currentDetail, remarks: e.target.value })}
                    placeholder="Remarks"
                    style={{ flex: 2 }}
                  />

                  <button
                    type="button"
                    className="btn-icon btn-add"
                    onClick={addDetailLine}
                    title="Add Line"
                  >
                    <Plus size={20} />
                  </button>
                </div>

                {errors.details && <span className="error-message">{errors.details}</span>}

                {formData.details.length > 0 && (
                  <div className="details-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Account</th>
                          <th>Amount</th>
                          <th>Remarks</th>
                          <th width="50">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.details.map((detail, index) => (
                          <tr key={index}>
                            <td>{detail.acheadname}</td>
                            <td className="amount-cell">{parseFloat(detail.amount).toFixed(2)}</td>
                            <td>{detail.remarks || '-'}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-icon btn-delete"
                                onClick={() => removeDetailLine(index)}
                                title="Remove"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="total-row">
                          <td><strong>Total</strong></td>
                          <td className="amount-cell"><strong>{calculateTotal().toFixed(2)}</strong></td>
                          <td colSpan="2"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="remarks">Remarks</label>
                <textarea
                  id="remarks"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows="3"
                  placeholder="Enter any additional remarks..."
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  <Ban size={16} />
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-print"
                  onClick={handlePrint}
                  disabled={formData.details.length === 0 || !formData.cashAccountId}
                  title="Print Voucher"
                >
                  <Printer size={16} />
                  Print
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading || formData.details.length === 0}
                >
                  <Save size={16} />
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashVoucher;